import { Form, Formik, type FormikHelpers } from "formik";
import type { AssetPosition, DeviceAssetNewInAssetTypeEnum } from "kubb";
import { assetClassEnum } from "kubb";
import { isNil } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import { ErrorBanner } from "../../../components/Error/ErrorBanner.tsx";
import NewTextFormComponent from "../../../components/Forms/NewTextFormComponent";
import { SelectComboField } from "../../../components/Forms/SelectCombo/SelectCombo";
import { Button } from "../../../components/Theme/button";
import { useCreateAsset } from "../../../hooks/createEntity";
import useRefreshAssetRegister from "../../../hooks/refreshAssetRegister.tsx";
import AssetTypeSelect from "../assets/AssetTypeSelect";
import AssetPositionAssetLinkDateSelect from "./assetPositionAssetLinks/AssetPositionAssetLinkDateSelect";

interface FormValues {
  serial_number: string;
  manufacturer: string;
  asset_type: "gateway" | DeviceAssetNewInAssetTypeEnum;
  install_date?: string;
  uninstall_date?: string;
}

interface Props {
  companyId: string;
  assetPosition?: AssetPosition;
  refresh?: () => void;
}

export default function CreateAssetForm(props: Props): React.ReactElement {
  const create = useCreateAsset(props.companyId);
  const [isGateway, setIsGateway] = React.useState<boolean>(true);
  const lockType = !isNil(props.assetPosition);
  React.useEffect(() => {
    if (props.assetPosition) {
      setIsGateway(props.assetPosition.asset_position_type === "gateway");
    }
  });

  const refresh = useRefreshAssetRegister({
    assetPositionId: props.assetPosition?.asset_position_id,
  });

  const submit = (
    values: FormValues,
    { setSubmitting, resetForm }: FormikHelpers<FormValues>,
  ): void => {
    setSubmitting(true);
    create
      .update({
        serial_number: values.serial_number,
        manufacturer: isGateway ? values.manufacturer : undefined,
        asset_class: isGateway
          ? values.manufacturer === "evinox"
            ? assetClassEnum.evinox
            : assetClassEnum.mbus
          : assetClassEnum.mbus,
        company_id: props.companyId,
        asset_type: values.asset_type,
        current_link: props.assetPosition
          ? {
              asset_position_id: props.assetPosition.asset_position_id,
              install_date: values.install_date,
              uninstall_date: values.uninstall_date,
              cancelled: false,
            }
          : undefined,
      })
      .then(() => {
        refresh();
        props.refresh?.();
        resetForm();
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const initialValues = {
    serial_number: "",
    manufacturer: isGateway ? "elvaco" : "",
    asset_type: props.assetPosition
      ? (props.assetPosition.asset_position_type as
          | "gateway"
          | DeviceAssetNewInAssetTypeEnum)
      : ("gateway" as "gateway" | DeviceAssetNewInAssetTypeEnum),
  };

  return (
    <Formik
      onSubmit={submit}
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        serial_number: Yup.string().required("Required"),
        manufacturer: isGateway
          ? Yup.string().required()
          : Yup.string().notRequired().max(0),
      })}
      validateOnMount={true}
      enableReinitialize={true}
    >
      {({ isSubmitting, isValid, values }) => (
        <Form data-cy={"create-asset-form"}>
          <>
            <NewTextFormComponent
              fieldName={"serial_number"}
              label={{ label: "Serial Number" }}
            />
            <AssetTypeSelect
              fieldName={"asset_type"}
              label={{ label: "Asset Type" }}
              disabled={lockType}
              onChange={(v) => setIsGateway(v === "gateway")}
            />
            {isGateway && (
              <SelectComboField
                fieldName={"manufacturer"}
                options={[
                  { label: "Elvaco", value: "elvaco" },
                  {
                    label: "Evinox",
                    value: "evinox",
                  },
                  { label: "Lansen", value: "lansen" },
                ]}
                label={{ label: "Manufacturer" }}
              />
            )}
            {props.assetPosition && (
              <>
                <h4>Dates</h4>
                <AssetPositionAssetLinkDateSelect newLink={true} />
              </>
            )}

            <Button
              type={"submit"}
              color={"brandLight"}
              disabled={isSubmitting || !isValid}
            >
              Add Asset
            </Button>
            {create.error && <ErrorBanner error={create.error} />}
          </>
        </Form>
      )}
    </Formik>
  );
}
